import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Alert, Box, Button, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import ThumbDown from '@mui/icons-material/ThumbDown';
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import { fetchEntitlements } from '../../store/entitlementsSlice';
import { clearIssueInsightRequest } from '../../store/aiInsightSlice';
import RestrictedMarkdown from './RestrictedMarkdown';
import CitationsPanel from './CitationsPanel';
import AiThinking from './AiThinking';
import DemoBanner from './DemoBanner';
import IssueInsightCard, { AI_NOTICE_TEXT } from './IssueInsightCard';
import * as aiClient from './aiClient';

const UNAVAILABLE_TEXT = 'The AI assistant is unavailable right now. Please try again later.';
const HISTORY_UNAVAILABLE_TEXT = 'Conversation history is unavailable.';

function toHistoryMessage(m) {
  if (!m || typeof m.id !== 'string' || typeof m.content !== 'string') return null;
  if (m.role !== 'user' && m.role !== 'assistant') return null;
  return { id: m.id, role: m.role, content: m.content, citations: [], notices: [], toolStatus: null, streaming: false };
}

export default function ChatPanel() {
  const dispatch = useDispatch();
  const store = useStore();
  const entitlements = useSelector((s) => s.entitlements);
  const hasFetchedRef = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
  const [conversations, setConversations] = useState([]);
  const [conversationsError, setConversationsError] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);

  const conversationIdRef = useRef(null);
  // Bumped on every org switch / lock so a history response requested under the previous org
  // is dropped instead of rendering under the new one (same idea as AiNotifications' cancel flag).
  const historyGenerationRef = useRef(0);
  // Set only by a launcher open, so the insight path's panel open never fires a history call.
  const launcherOpenRef = useRef(false);
  const historyLoadedGenerationRef = useRef(null);
  const abortControllerRef = useRef(null);
  const userStoppedRef = useRef(false);
  const timedOutRef = useRef(false);

  const currentOrgId = useSelector((s) => s.organizations?.currentOrgId ?? null);
  const prevOrgIdRef = useRef(currentOrgId);

  const insightRequest = useSelector((s) => s.aiInsight?.request ?? null);
  const handledInsightRequestIdRef = useRef(null);

  const locked = entitlements.ai_assistant !== true;
  const prevLockedRef = useRef(locked);

  const loadConversations = useCallback(() => {
    const generation = historyGenerationRef.current;
    setConversationsError(false);
    aiClient.listConversations()
      .then((list) => {
        if (generation !== historyGenerationRef.current) return;
        setConversations(list.filter((c) => c && typeof c.id === 'string'));
      })
      .catch(() => {
        // History is secondary to chatting, so a failed list shows inline instead of
        // switching the whole panel to the unavailable state.
        if (generation !== historyGenerationRef.current) return;
        setConversationsError(true);
      });
  }, []);

  // Founder decision 2026-10-17: entitlements must be fetched on mount, not only when
  // the panel opens — ResolutionButtons on other pages reads this same entitlements
  // state and must not wait for the user to open this panel first.
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchEntitlements());
    }
  }, [dispatch]);

  // In-session org switching (OrgSwitcher.jsx -> switchOrganization thunk) re-mints the
  // session for a different org with no page reload and no remount of this component, so
  // without this effect a previous org's conversation (and a conversationId scoped to its
  // session) would keep rendering under the new org. Clear the conversation/stream state and
  // re-resolve ai_assistant, which is per-org, for the new one.
  useEffect(() => {
    if (prevOrgIdRef.current === currentOrgId) return;
    prevOrgIdRef.current = currentOrgId;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    userStoppedRef.current = false;
    timedOutRef.current = false;
    conversationIdRef.current = null;
    historyGenerationRef.current += 1;
    setMessages([]);
    setStreaming(false);
    setUnavailable(false);
    setInputValue('');
    setConversations([]);
    setConversationsError(false);
    setActiveConversationId(null);
    dispatch(fetchEntitlements());
  }, [currentOrgId, dispatch]);

  // Founder decision 2026-10-17: a flag flip to locked while the panel is already open (e.g.
  // an org switch whose entitlements resolve ai_assistant:false) must close the panel and drop
  // its conversation state, not leave it mounted behind an unreachable locked branch.
  useEffect(() => {
    if (prevLockedRef.current === locked) return;
    prevLockedRef.current = locked;
    if (!locked) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    userStoppedRef.current = false;
    timedOutRef.current = false;
    conversationIdRef.current = null;
    historyGenerationRef.current += 1;
    setIsOpen(false);
    setMessages([]);
    setStreaming(false);
    setUnavailable(false);
    setInputValue('');
    setConversations([]);
    setConversationsError(false);
    setActiveConversationId(null);
  }, [locked]);

  // The list loads once per launcher open and org (generation), and only once the store says the
  // current org's flag has resolved true. Read from the store, not this render: right after an org
  // switch the render still carries the previous org's ai_assistant while the new fetch is pending.
  useEffect(() => {
    if (!isOpen) {
      launcherOpenRef.current = false;
      historyLoadedGenerationRef.current = null;
      return;
    }
    if (!launcherOpenRef.current) return;
    const current = store.getState().entitlements;
    if (current.loading || current.ai_assistant !== true) return;
    if (historyLoadedGenerationRef.current === historyGenerationRef.current) return;
    historyLoadedGenerationRef.current = historyGenerationRef.current;
    loadConversations();
  }, [isOpen, locked, entitlements.loading, currentOrgId, store, loadConversations]);

  // Resolution buttons (AI-UI.7) on finding/CVE/asset/control pages dispatch
  // requestIssueInsight instead of rendering their own insight UI; this effect is what opens
  // this already-existing panel and reuses its locked/unavailable states and message rendering.
  useEffect(() => {
    if (!insightRequest) return;
    if (handledInsightRequestIdRef.current === insightRequest.requestId) return;

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchEntitlements());
      return;
    }
    if (entitlements.loading) return;

    handledInsightRequestIdRef.current = insightRequest.requestId;

    if (locked) {
      dispatch(clearIssueInsightRequest());
      return;
    }

    setIsOpen(true);

    const { entityType, entityId, kind } = insightRequest;
    const label = kind === 'fix' ? 'Possible fix' : 'Explain issue';
    const assistantMessageId = `assistant-${Date.now()}-${Math.random()}`;
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}-${Math.random()}`, role: 'user', content: `${label}: ${entityId}`, citations: [], notices: [], toolStatus: null, streaming: false },
      { id: assistantMessageId, role: 'assistant', content: '', citations: [], notices: [], toolStatus: null, streaming: false, waiting: true },
    ]);

    aiClient.getIssueInsight({ entityType, entityId })
      .then((result) => {
        if (result.notFound) {
          setMessages((prev) => prev.map((m) => (
            m.id === assistantMessageId ? { ...m, content: result.message, waiting: false } : m
          )));
          return;
        }
        if (typeof result?.summary !== 'string') {
          setUnavailable(true);
          return;
        }
        setMessages((prev) => prev.map((m) => (
          m.id === assistantMessageId
            ? { id: assistantMessageId, role: 'insight', insight: result, citations: [], notices: [], toolStatus: null, streaming: false }
            : m
        )));
      })
      .catch(() => {
        setUnavailable(true);
      })
      .finally(() => {
        dispatch(clearIssueInsightRequest());
      });
  }, [insightRequest, entitlements.loading, locked, dispatch]);

  // History loads only on a launcher open (effect above); the insight path opens the panel for
  // its own single request and leaves the list for the next launcher open.
  const handleToggle = () => {
    const opening = !isOpen;
    launcherOpenRef.current = opening;
    setIsOpen(opening);
  };

  const handleSelectConversation = (id) => {
    if (streaming) return;
    const generation = historyGenerationRef.current;
    conversationIdRef.current = id;
    setActiveConversationId(id);
    setMessages([]);
    setUnavailable(false);
    aiClient.listMessages(id)
      .then((list) => {
        if (generation !== historyGenerationRef.current || conversationIdRef.current !== id) return;
        setMessages(list.map(toHistoryMessage).filter(Boolean));
      })
      .catch(() => {
        if (generation !== historyGenerationRef.current || conversationIdRef.current !== id) return;
        setUnavailable(true);
      });
  };

  const handleNewConversation = () => {
    if (streaming) return;
    conversationIdRef.current = null;
    setActiveConversationId(null);
    setMessages([]);
    setUnavailable(false);
  };

  const ensureConversation = async () => {
    if (conversationIdRef.current) return conversationIdRef.current;
    const generation = historyGenerationRef.current;
    const conversation = await aiClient.createConversation();
    // The org switched while the create was in flight: abandon the turn rather than send an
    // Org A conversation's write under the new org's session.
    if (generation !== historyGenerationRef.current) return null;
    conversationIdRef.current = conversation.id;
    if (typeof conversation.id === 'string') {
      setConversations((prev) => (
        prev.some((c) => c.id === conversation.id) ? prev : [conversation, ...prev]
      ));
      setActiveConversationId(conversation.id);
    }
    return conversation.id;
  };

  const appendDelta = (assistantMessageId, delta) => {
    setMessages((prev) => prev.map((m) => (
      m.id === assistantMessageId ? { ...m, content: m.content + (delta || '') } : m
    )));
  };

  const appendCitation = (assistantMessageId, citation) => {
    setMessages((prev) => prev.map((m) => (
      m.id === assistantMessageId ? { ...m, citations: [...m.citations, citation] } : m
    )));
  };

  const appendNotice = (assistantMessageId, notice) => {
    setMessages((prev) => prev.map((m) => (
      m.id === assistantMessageId ? { ...m, notices: [...(m.notices || []), notice] } : m
    )));
  };

  const setToolStatus = (assistantMessageId, toolStatus) => {
    setMessages((prev) => prev.map((m) => (
      m.id === assistantMessageId ? { ...m, toolStatus } : m
    )));
  };

  const markStreamEnded = (assistantMessageId) => {
    setMessages((prev) => prev.map((m) => (
      m.id === assistantMessageId ? { ...m, streaming: false } : m
    )));
  };

  const handleSend = async () => {
    const content = inputValue.trim();
    if (!content || streaming) return;
    setInputValue('');

    let conversationId;
    try {
      conversationId = await ensureConversation();
    } catch (err) {
      setUnavailable(true);
      return;
    }
    if (conversationId === null) return;

    const assistantMessageId = `assistant-${Date.now()}-${Math.random()}`;
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}-${Math.random()}`, role: 'user', content, citations: [], notices: [], toolStatus: null, streaming: false },
      { id: assistantMessageId, role: 'assistant', content: '', citations: [], notices: [], toolStatus: null, streaming: true },
    ]);
    setStreaming(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    userStoppedRef.current = false;
    timedOutRef.current = false;

    const timeoutId = setTimeout(() => {
      timedOutRef.current = true;
      abortController.abort();
      markStreamEnded(assistantMessageId);
      setStreaming(false);
      setUnavailable(true);
    }, aiClient.AI_REQUEST_TIMEOUT_MS);

    const onEvent = (evt) => {
      if (userStoppedRef.current) return;
      if (!evt || typeof evt.event !== 'string') return;
      if (evt.event === 'message.delta') {
        appendDelta(assistantMessageId, evt.delta);
      } else if (evt.event === 'citation') {
        const { event, ...citation } = evt;
        appendCitation(assistantMessageId, citation);
      } else if (evt.event === 'notice') {
        if (typeof evt.message === 'string') {
          appendNotice(assistantMessageId, { code: evt.code, message: evt.message });
        }
      } else if (evt.event === 'tool.status') {
        if (typeof evt.tool === 'string' && typeof evt.status === 'string') {
          setToolStatus(assistantMessageId, { tool: evt.tool, status: evt.status });
        }
      } else if (evt.event === 'done') {
        markStreamEnded(assistantMessageId);
        setStreaming(false);
      } else if (evt.event === 'error') {
        markStreamEnded(assistantMessageId);
        setStreaming(false);
        setUnavailable(true);
      }
    };

    try {
      await aiClient.sendMessage({ conversationId, content, signal: abortController.signal, onEvent });
    } catch (err) {
      if (err && err.name === 'AbortError') {
        markStreamEnded(assistantMessageId);
        setStreaming(false);
        if (timedOutRef.current) {
          setUnavailable(true);
        }
      } else {
        markStreamEnded(assistantMessageId);
        setStreaming(false);
        setUnavailable(true);
      }
    } finally {
      clearTimeout(timeoutId);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    userStoppedRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStreaming(false);
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
  };

  // Submits feedback for a single message and updates only that message's selection state, so
  // the rest of `messages` keeps its object identity and never re-renders (A4).
  const handleFeedback = (messageId, rating) => {
    aiClient.submitFeedback({ messageId, rating })
      .then(() => {
        setFeedbackByMessageId((prev) => ({ ...prev, [messageId]: rating }));
      })
      .catch(() => {
        // A failed feedback submission must not lock/break the chat (it's not part of the
        // send/stream flow), so it's dropped silently and the control stays unselected.
      });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (locked) return null;

  return (
    <>
      <Button
        variant="contained"
        aria-label={isOpen ? 'Close AI Assistant chat' : 'Open AI Assistant chat'}
        onClick={handleToggle}
        data-testid="ai-chat-toggle"
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}
      >
        {isOpen ? 'Close' : 'AI Assistant'}
      </Button>

      {isOpen && (
        <Paper
          elevation={8}
          data-testid="ai-chat-panel"
          sx={{ position: 'fixed', bottom: 80, right: 24, width: 360, zIndex: 1300, maxHeight: '70vh', overflowY: 'auto', p: 1.5 }}
        >
          <DemoBanner testId="ai-chat-demo-banner" />

          {unavailable ? (
            <Alert severity="warning" data-testid="ai-chat-unavailable">{UNAVAILABLE_TEXT}</Alert>
          ) : (
            <Box data-testid="ai-chat-active">
              <Typography variant="caption" component="div" data-testid="ai-chat-notice">{AI_NOTICE_TEXT}</Typography>
              <Stack data-testid="ai-conversation-list" spacing={0.5} sx={{ my: 1 }}>
                <Button
                  size="small"
                  data-testid="ai-conversation-new"
                  onClick={handleNewConversation}
                  disabled={streaming}
                >
                  New conversation
                </Button>
                {conversations.map((c) => (
                  <Button
                    key={c.id}
                    size="small"
                    data-testid={`ai-conversation-${c.id}`}
                    aria-current={c.id === activeConversationId}
                    onClick={() => handleSelectConversation(c.id)}
                    disabled={streaming}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    {typeof c.title === 'string' && c.title ? c.title : String(c.createdAt ?? '')}
                  </Button>
                ))}
                {conversationsError && (
                  <Alert severity="error" data-testid="ai-conversation-list-error">{HISTORY_UNAVAILABLE_TEXT}</Alert>
                )}
              </Stack>
              <Box data-testid="ai-chat-messages">
                {messages.map((m) => (m.role === 'insight' ? (
                  <Box key={m.id} data-role="insight" sx={{ mb: 1 }}>
                    <IssueInsightCard insight={m.insight} />
                  </Box>
                ) : (
                  <Box key={m.id} data-role={m.role} sx={{ mb: 1 }}>
                    {m.role === 'assistant' && !m.content && (m.streaming || m.waiting) && !unavailable ? (
                      <AiThinking />
                    ) : m.role === 'assistant' ? (
                      <RestrictedMarkdown content={m.content} />
                    ) : (
                      <Typography variant="body2">{m.content}</Typography>
                    )}
                    {m.streaming && m.toolStatus && (
                      <Typography variant="caption" component="div" data-testid="ai-tool-status" role="status">
                        {`${m.toolStatus.tool}: ${m.toolStatus.status}`}
                      </Typography>
                    )}
                    {(m.notices || []).map((n, idx) => (
                      <Alert key={idx} severity="info" role="note" data-testid="ai-message-notice" sx={{ my: 0.5 }}>
                        {n.message}
                      </Alert>
                    ))}
                    <CitationsPanel citations={m.citations} />
                    {m.role === 'assistant' && (
                      <Box>
                        <IconButton
                          size="small"
                          aria-label="Feedback: thumbs up"
                          aria-pressed={feedbackByMessageId[m.id] === 'up'}
                          data-testid={`ai-feedback-up-${m.id}`}
                          onClick={() => handleFeedback(m.id, 'up')}
                        >
                          {feedbackByMessageId[m.id] === 'up' ? <ThumbUp fontSize="small" /> : <ThumbUpOutlined fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Feedback: thumbs down"
                          aria-pressed={feedbackByMessageId[m.id] === 'down'}
                          data-testid={`ai-feedback-down-${m.id}`}
                          onClick={() => handleFeedback(m.id, 'down')}
                        >
                          {feedbackByMessageId[m.id] === 'down' ? <ThumbDown fontSize="small" /> : <ThumbDownOutlined fontSize="small" />}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )))}
              </Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  disabled={streaming}
                  inputProps={{ 'aria-label': 'Message the AI assistant' }}
                />
                {streaming ? (
                  <Button color="error" variant="outlined" aria-label="Stop generating response" onClick={handleStop}>
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={inputValue.trim().length === 0}
                  >
                    Send
                  </Button>
                )}
              </Stack>
            </Box>
          )}
        </Paper>
      )}
    </>
  );
}
